import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Hr from "@saleor/components/Hr";
import TableHead from "@saleor/components/TableHead";
import { VoucherBrandGroupsDetail_voucher } from "@saleor/discounts/types/VoucherBrandGroupsDetail";
import { VoucherDetails } from "@saleor/discounts/types/VoucherDetails";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchBrandGroup_search_edges_node } from "@saleor/searches/types/SearchBrandGroup";
import { FetchMoreProps } from "@saleor/types";
import { VoucherBrandGroupsInput } from "@saleor/types/globalTypes";
import createMultiAutocompleteSelectHandler from "@saleor/utils/handlers/multiAutocompleteSelectChangeHandler";
import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import CardSpacer from "../CardSpacer";
import Form from "../Form/Form";
import { Grid } from "../Grid";
import MultiAutocompleteSelectFieldNew from "../MultiAutocompleteSelectFieldNew";
import ResponsiveTable from "../ResponsiveTable";
import useStyles from "./styles";

export interface StoreSubsidyItem {
  id: string;
  name: string;
  tenantSubsidy: string;
  smoSubsidy: string;
}

export interface StoreVariantSelection {
  [x: string]: any;
  value: string;
  label: string;
  tenantSubsidy?: string;
  smoSubsidy?: string;
}

interface FormData {
  isAppliedToAll: boolean;
  brandGroups: any[];
  brandGroupAppliedToAll: any[];
  brandGroupSmoShares: { [key: string]: string };
  brandGroupTenantShares: { [key: string]: string };
  tenantSmoShares: { [key: string]: string };
  tenantShares: { [key: string]: string };
  brandTenants: any[];
  appliedToAllSMO: number | string;
  appliedToAllTenant: number | string;
  removedTenants: { [key: string]: string };
}

const initialForm: FormData = {
  appliedToAllSMO: "",
  appliedToAllTenant: "0",
  brandGroupAppliedToAll: [],
  brandGroupSmoShares: {},
  brandGroupTenantShares: {},
  brandGroups: [],
  brandTenants: [],
  isAppliedToAll: true,
  removedTenants: {},
  tenantShares: {},
  tenantSmoShares: {}
};

const steps: { [key: string]: string } = {
  assignBrand: "Assign Brands",
  assignDiscount: "Assign Store and Discounts"
};

interface Tenant {
  id: string;
  storeName: string;
  brandGroupId: string;
}

interface BrandGroupOption {
  label: string;
  tenants: Tenant[];
  value: string;
}

interface BrandGroup {
  id: string;
  sellers: any[];
  name: string;
}

const toInt = data => Math.max(data, 0);

const translateTenants = (brandGroup: BrandGroup): Tenant[] => {
  const sellers = maybe(() => brandGroup.sellers, []);
  const tenants = [];

  sellers.forEach(seller => {
    const sellerTenants = maybe(() => seller.tenants, []);
    sellerTenants.forEach(tenant => {
      tenants.push({
        ...tenant,
        brandGroupId: brandGroup.id
      });
    });
  });
  return tenants;
};

const translateAvailableBrandGroups = (
  brandGroups: BrandGroup[]
): BrandGroupOption[] =>
  brandGroups.map((brandGroup: BrandGroup) => ({
    label: brandGroup?.name,
    tenants: translateTenants(brandGroup),
    value: brandGroup.id
  }));

export interface BrandGroupDialogProps {
  data: VoucherDetails;
  confirmButtonState: ConfirmButtonTransitionState;
  sellers: SearchBrandGroup_search_edges_node[];
  fetchMoreSellers: FetchMoreProps;
  tenants: VoucherBrandGroupsDetail_voucher;
  isNationwideVoucher: boolean;
  open: boolean;
  onClose: () => void;
  fetchSellers: (query: string) => void;
  onSubmit: (data: VoucherBrandGroupsInput) => void;
}

const BrandGroupDialog: React.FC<BrandGroupDialogProps> = props => {
  const {
    sellers: brandGroupsList,
    tenants,
    isNationwideVoucher,
    fetchMoreSellers,
    fetchSellers,
    onSubmit,
    onClose,
    open
  } = props;

  const intl = useIntl();
  const classes = useStyles(props);
  const availableBrandGroups = translateAvailableBrandGroups(brandGroupsList);

  const [step, setStep] = useState("assignBrand");
  const [title, setTitle] = useState("Assign Brands");
  const [selectedBrandGroups, setSelectedBrandGroups] = useState([]);
  const [selectAllDropdown, setSelectAllDropdown] = useState(false);
  const [hideDeleteIcon, setHideDeleteIcon] = useState(true);
  const existingTenantsList = tenants?.voucherTenants.map(tenant =>
    `${tenant.tenant.brand.name}-${tenant.tenant.mall.name}`.replace(/\s+/g, "")
  );

  useEffect(() => {
    setTitle(steps[step]);
  }, [step]);

  const mergedBrandGroups = [
    ...availableBrandGroups,
    ...selectedBrandGroups.filter(
      selected =>
        !availableBrandGroups.some(
          available => available.value === selected.value
        )
    )
  ];

  useEffect(() => {
    if (step === "assignDiscount" && selectedBrandGroups.length === 0) {
      setStep("assignBrand");
    }
  }, [selectedBrandGroups]);

  const handleCloseDialog = () => {
    setSelectAllDropdown(false);
    setSelectedBrandGroups([]);
    onClose();
  };

  const extractTenantShare = (brandGroupId, tenantId, data) => {
    const {
      isAppliedToAll,
      appliedToAllSMO,
      appliedToAllTenant,
      brandGroupSmoShares,
      brandGroupTenantShares,
      brandGroupAppliedToAll,
      tenantSmoShares,
      tenantShares
    } = data;

    if (isAppliedToAll) {
      return {
        smoDiscountShare: appliedToAllSMO,
        tenantDiscountShare: appliedToAllTenant
      };
    }

    if (brandGroupAppliedToAll.includes(brandGroupId)) {
      return {
        smoDiscountShare: maybe(() => brandGroupSmoShares[brandGroupId], 100),
        tenantDiscountShare: maybe(
          () => brandGroupTenantShares[brandGroupId],
          0
        )
      };
    }

    return {
      smoDiscountShare: maybe(() => tenantSmoShares[tenantId], 100),
      tenantDiscountShare: maybe(() => tenantShares[tenantId], 0)
    };
  };

  const handleSubmit = event => {
    const {
      brandGroups,
      brandGroupAppliedToAll,
      removedTenants,
      isAppliedToAll,
      appliedToAllSMO,
      appliedToAllTenant,
      brandGroupSmoShares,
      brandGroupTenantShares
    } = event;

    let payload = {
      isAppliedToAllBrandGroups: isAppliedToAll,
      ...(isAppliedToAll && {
        smoDiscountShare: appliedToAllSMO
      }),
      ...(isAppliedToAll && {
        tenantDiscountShare: appliedToAllTenant
      })
    };

    if (brandGroups.length === 0) {
      return;
    }

    payload = {
      ...payload,
      ...{
        brandGroups: brandGroups.map(brandId => {
          const appliedToAllStores = brandGroupAppliedToAll.includes(brandId);
          const removedBrandTenants = maybe(() => removedTenants[brandId], []);
          const tenants = maybe(
            () =>
              selectedBrandGroups.find(
                brandGroup => brandGroup.value === brandId
              ).tenants,
            []
          )
            .filter(tenant => !removedBrandTenants.includes(tenant.id))
            .map(tenant => ({
              tenant: tenant.id,
              ...extractTenantShare(brandId, tenant.id, event)
            }));
          return {
            brandGroup: brandId,
            isAppliedToAllTenants: appliedToAllStores,
            ...(appliedToAllStores && {
              smoDiscountShare: maybe(() => brandGroupSmoShares[brandId], 100)
            }),
            ...(appliedToAllStores && {
              tenantDiscountShare: maybe(
                () => brandGroupTenantShares[brandId],
                0
              )
            }),
            tenants
          };
        })
      }
    };
    onSubmit(payload);
    handleCloseDialog();
  };

  return (
    <>
      <Dialog
        onClose={handleCloseDialog}
        open={open}
        fullWidth
        maxWidth="md"
        className={step === "assignBrand" ? classes.dialogContainer : null}
      >
        <Form initial={initialForm} onSubmit={handleSubmit}>
          {({ data, change, toggleValue, submit }) => {
            const handleSelectBrandGroup = createMultiAutocompleteSelectHandler(
              toggleValue,
              setSelectedBrandGroups,
              selectedBrandGroups,
              mergedBrandGroups
            );

            useEffect(() => {
              if (data.isAppliedToAll) {
                change({
                  target: {
                    name: "appliedToAllSMO",
                    value: 100
                  }
                });
              }

              if (data.isAppliedToAll) {
                setHideDeleteIcon(true);
                change({
                  target: {
                    name: "removedTenants",
                    value: {}
                  }
                });
              } else {
                setHideDeleteIcon(false);
              }
            }, [data.isAppliedToAll, data.brandGroupAppliedToAll.length]);

            const handleSelectAll = event => {
              setSelectAllDropdown(event);
              if (event) {
                const uniqueBrandGroups = Array.from(
                  new Map(
                    mergedBrandGroups.map(item => [item.value, item])
                  ).values()
                );

                change({
                  target: {
                    name: "brandGroups",
                    value: uniqueBrandGroups.map(brandGroup => brandGroup.value)
                  }
                });

                setSelectedBrandGroups(uniqueBrandGroups);
              } else {
                change({
                  target: {
                    name: "brandGroups",
                    value: []
                  }
                });
                setSelectedBrandGroups([]);
              }
            };

            const handleApplyToAll = event => {
              const { name } = event.target;
              if (name === "all") {
                change({
                  target: {
                    name: "isAppliedToAll",
                    value: event.target.checked
                  }
                });

                change({
                  target: {
                    name: "brandGroupAppliedToAll",
                    value: []
                  }
                });
              } else {
                if (
                  !data.brandGroupAppliedToAll.includes(name) &&
                  event.target.checked
                ) {
                  data.brandGroupAppliedToAll.push(name);
                  delete data.removedTenants[name];
                  change({
                    target: {
                      name: "removedTenants",
                      value: data.removedTenants
                    }
                  });
                }

                if (
                  data.brandGroupAppliedToAll.includes(name) &&
                  !event.target.checked
                ) {
                  data.brandGroupAppliedToAll.splice(
                    data.brandGroupAppliedToAll.indexOf(name),
                    1
                  );
                }

                change({
                  target: {
                    name: "brandGroupAppliedToAll",
                    value: data.brandGroupAppliedToAll
                  }
                });
              }
            };

            const handleSubsidyChange = (target, type, event) => {
              const { name, value } = event.target;

              if (target === "all") {
                change({
                  target: {
                    name:
                      type === "smo" ? "appliedToAllSMO" : "appliedToAllTenant",
                    value: toInt(value)
                  }
                });
                return;
              }

              if (name) {
                const targetShares = {
                  brandGroup: {
                    smo: "brandGroupSmoShares",
                    tenant: "brandGroupTenantShares"
                  },
                  singleTenant: {
                    smo: "tenantSmoShares",
                    tenant: "tenantShares"
                  }
                };

                const targetShare = targetShares[target][type];
                change({
                  target: {
                    name: targetShare,
                    value: {
                      ...data[targetShare],
                      [name]: toInt(value)
                    }
                  }
                });
              }
            };

            const handleRemoveTenants = ({ value, tenants }, tenantId) => {
              if (!data.removedTenants[value]) {
                data.removedTenants[value] = [tenantId];
              }

              if (!data.removedTenants[value].includes(tenantId)) {
                data.removedTenants[value] = [
                  ...data.removedTenants[value],
                  tenantId
                ];
              }

              change({
                target: {
                  name: "removedTenants",
                  value: data.removedTenants
                }
              });

              if (tenants.length === data.removedTenants[value].length) {
                setSelectedBrandGroups(
                  selectedBrandGroups.filter(
                    brandGroup => brandGroup.value !== value
                  )
                );
                change({
                  target: {
                    name: "brandGroups",
                    value: data.brandGroups.filter(id => id !== value)
                  }
                });

                delete data.removedTenants[value];
                change({
                  target: {
                    name: "removedTenants",
                    value: data.removedTenants
                  }
                });
              }
            };

            return (
              <>
                <DialogTitle className={classes.assignBrands}>
                  {title}
                </DialogTitle>

                {step === "assignBrand" ? (
                  <DialogContent className={classes.discountContent}>
                    <Box>
                      <MultiAutocompleteSelectFieldNew
                        responsiveHeight={true}
                        id="assignBrandGroupMultiTag"
                        displayValues={selectedBrandGroups}
                        label={intl.formatMessage({
                          defaultMessage: "Seller (Brand)"
                        })}
                        choices={availableBrandGroups}
                        name="brandGroups"
                        value={data?.brandGroups || []}
                        onChange={handleSelectBrandGroup}
                        hasSelectAll={isNationwideVoucher}
                        onHandleSelectAll={handleSelectAll}
                        selectAll={selectAllDropdown}
                        fetchChoices={fetchSellers}
                        {...fetchMoreSellers}
                      />
                    </Box>
                  </DialogContent>
                ) : (
                  <Box>
                    <Box className={classes.applyToAllSelectedBrands}>
                      <Grid variant="uniform" className={classes.allBrandsText}>
                        <Box>
                          <Checkbox
                            id="isAppliedToAllCheckbox"
                            name="all"
                            checked={data.isAppliedToAll}
                            value={data.isAppliedToAll}
                            onChange={handleApplyToAll}
                          />
                          <Typography component="span">
                            Apply to all selected brands
                          </Typography>
                        </Box>
                        <Box>
                          <Typography className={classes.applyAll}>
                            <FormattedMessage
                              defaultMessage="*Percentage values will apply on all selected brands"
                              description="percentage values will apply on all selected brands"
                            />
                          </Typography>
                        </Box>
                      </Grid>
                      {data.isAppliedToAll ? (
                        <Grid
                          variant="uniform"
                          className={classes.allBrandsTextField}
                        >
                          <Box>
                            <TextField
                              id="appliedToAllTenantTxt"
                              name="appliedToAllTenant"
                              label={intl.formatMessage({
                                defaultMessage: "Tenant Discount",
                                description: "tenant discount"
                              })}
                              defaultValue={0}
                              value={data.appliedToAllTenant}
                              onChange={event =>
                                handleSubsidyChange("all", "tenant", event)
                              }
                              onKeyPress={event => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              fullWidth
                              InputProps={{
                                endAdornment: "%"
                              }}
                            />
                          </Box>
                          <Box>
                            <TextField
                              id="appliedToAllSMOTxt"
                              name="appliedToAllSMO"
                              label={intl.formatMessage({
                                defaultMessage: "SMO Discount",
                                description: "smo discount"
                              })}
                              value={data.appliedToAllSMO}
                              onChange={event =>
                                handleSubsidyChange("all", "smo", event)
                              }
                              onKeyPress={event => {
                                if (!/[0-9]/.test(event.key)) {
                                  event.preventDefault();
                                }
                              }}
                              fullWidth
                              InputProps={{
                                endAdornment: "%"
                              }}
                            />
                          </Box>
                        </Grid>
                      ) : null}
                    </Box>
                    <DialogContent>
                      {selectedBrandGroups.length
                        ? selectedBrandGroups.map((brandGroup, index) => (
                            <BrandGroupItem
                              existingTenantList={existingTenantsList}
                              hideDeleteIcon={hideDeleteIcon}
                              brandGroup={brandGroup}
                              index={index}
                              data={data}
                              key={index}
                              handleSubsidyChange={handleSubsidyChange}
                              handleApplyToAll={handleApplyToAll}
                              handleRemoveTenants={handleRemoveTenants}
                            />
                          ))
                        : null}
                    </DialogContent>
                  </Box>
                )}

                <DialogActions>
                  {step === "assignBrand" ? (
                    <>
                      <Button
                        id="cancelBtn"
                        onClick={handleCloseDialog}
                        type="button"
                      >
                        <FormattedMessage {...buttonMessages.cancel} />
                      </Button>
                      <ConfirmButton
                        id="nextBtn"
                        disabled={data.brandGroups.length === 0}
                        transitionState={undefined}
                        color="primary"
                        variant="contained"
                        type="button"
                        onClick={() => setStep("assignDiscount")}
                      >
                        <FormattedMessage
                          defaultMessage="Next"
                          description="button"
                        />
                      </ConfirmButton>
                    </>
                  ) : (
                    <>
                      <Button
                        id="backBtn"
                        onClick={() => setStep("assignBrand")}
                        type="button"
                      >
                        <FormattedMessage {...buttonMessages.back} />
                      </Button>
                      <ConfirmButton
                        disabled={data.brandGroups.length === 0}
                        transitionState={undefined}
                        color="primary"
                        variant="contained"
                        onClick={submit}
                      >
                        <FormattedMessage
                          defaultMessage="Save"
                          description="button"
                        />
                      </ConfirmButton>
                    </>
                  )}
                </DialogActions>
              </>
            );
          }}
        </Form>
      </Dialog>
    </>
  );
};

interface BrandGroupItemProps {
  brandGroup: BrandGroupOption;
  index: number;
  hideDeleteIcon: boolean;
  data: FormData;
  existingTenantList: any[];
  handleSubsidyChange: (target, type, FormEvent) => void;
  handleApplyToAll: (event: any) => void;
  handleRemoveTenants: ({ value, tenants }, tenantId: string) => void;
}

const BrandGroupItem: React.FC<BrandGroupItemProps> = props => {
  const {
    brandGroup,
    index,
    data,
    existingTenantList,
    hideDeleteIcon,
    handleSubsidyChange,
    handleApplyToAll,
    handleRemoveTenants
  } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const isAppliedToAllStores =
    data.isAppliedToAll ||
    !data.brandGroupAppliedToAll.includes(brandGroup.value);

  return (
    <Box
      style={{
        marginBottom: 5
      }}
    >
      <Grid variant="uniform">
        <Box>
          <Typography component="span" className={classes.storeNumberList}>
            {index + 1}
          </Typography>
          <Typography
            component="span"
            className={classes.sellerName}
            style={{
              marginBottom: 2
            }}
          >
            Seller (Brand) Name
          </Typography>
          <Typography component="div" className={classes.selectedSellerName}>
            {brandGroup.label}
          </Typography>
        </Box>
        {data.isAppliedToAll ? null : (
          <Box className={classes.appliedToAllStore}>
            <Box>
              <Checkbox
                id={`applyToAllBrandCheckbox${index}`}
                name={brandGroup.value}
                checked={data.brandGroupAppliedToAll.includes(brandGroup.value)}
                onChange={handleApplyToAll}
              />
              <Typography component="span" style={{ marginBottom: 2 }}>
                Apply to all Stores
              </Typography>
            </Box>
            <Typography component="span" className={classes.applyAllStore}>
              Available stores listed are affected when this is checked.
            </Typography>
          </Box>
        )}
      </Grid>
      {data.isAppliedToAll ? null : (
        <>
          <Box>
            <Typography className={classes.applyAllSelectedStore}>
              <FormattedMessage
                defaultMessage="*Percentage values will apply on all stores"
                description="percentage values will apply on all stores"
              />
            </Typography>
          </Box>
          <Grid variant="uniform">
            <Box>
              <TextField
                id={`brand${index}TenantDiscountTxt`}
                disabled={isAppliedToAllStores}
                name={brandGroup.value}
                label={intl.formatMessage({
                  defaultMessage: "Tenant Discount",
                  description: "tenant discount"
                })}
                fullWidth
                defaultValue={0}
                InputProps={{
                  endAdornment: "%"
                }}
                onChange={event =>
                  handleSubsidyChange("brandGroup", "tenant", event)
                }
                onKeyPress={event => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Box>
            <Box>
              <TextField
                id={`brand${index}SMODiscountTxt`}
                disabled={isAppliedToAllStores}
                name={brandGroup.value}
                label={intl.formatMessage({
                  defaultMessage: "SMO Discount",
                  description: "smo discount"
                })}
                defaultValue={100}
                fullWidth
                onKeyPress={event => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: "%"
                }}
                onChange={event =>
                  handleSubsidyChange("brandGroup", "smo", event)
                }
              />
            </Box>
          </Grid>
        </>
      )}

      <BrandTenants
        hideDeleteIcon={hideDeleteIcon}
        existingTenantList={existingTenantList}
        tenants={brandGroup.tenants}
        brandIndex={index}
        key={`brandTenants${index}`}
        data={data}
        handleSubsidyChange={handleSubsidyChange}
        brandGroup={brandGroup}
        handleRemoveTenants={handleRemoveTenants}
      />
    </Box>
  );
};

interface BrandTenantsProps {
  tenants: any[];
  data: FormData;
  brandIndex: number;
  existingTenantList: any[];
  hideDeleteIcon: boolean;
  handleSubsidyChange: (target, type, FormEvent) => void;
  brandGroup: BrandGroupOption;
  handleRemoveTenants: (brandGroup: BrandGroupOption, tenantId: string) => void;
}

const BrandTenants: React.FC<BrandTenantsProps> = props => {
  const {
    tenants,
    hideDeleteIcon,
    data,
    brandIndex,
    existingTenantList,
    handleSubsidyChange,
    brandGroup,
    handleRemoveTenants
  } = props;

  const classes = useStyles(props);
  const canInputIndividual =
    !data.isAppliedToAll &&
    !data.brandGroupAppliedToAll.includes(brandGroup.value);

  const removeTenant = tenantId => {
    handleRemoveTenants(brandGroup, tenantId);
  };
  const [displayStore, setDisplayStore] = useState(false);

  return (
    <Box
      style={{
        marginBottom: 3
      }}
    >
      <CardSpacer />
      {displayStore ? <Hr className={classes.dashed} /> : null}
      <Box
        style={{
          display: displayStore ? "block" : "none"
        }}
      >
        <ResponsiveTable key="table">
          <TableHead colSpan={4} disabled={false} items={[]} hideCheckBox>
            <TableCell className={classes.colName}>
              <FormattedMessage defaultMessage="Store Name" />
            </TableCell>
            {hideDeleteIcon || !canInputIndividual ? null : (
              <>
                <TableCell className={classes.colPrice}>
                  <FormattedMessage defaultMessage="Tenant Discount" />
                </TableCell>
                <TableCell className={classes.colPrice}>
                  <FormattedMessage defaultMessage="SMOP Discount" />
                </TableCell>
                <TableCell className={classes.colActions} />
              </>
            )}
          </TableHead>
          <TableBody>
            {tenants.length ? (
              tenants
                .filter(
                  tenant =>
                    !maybe(
                      () => data.removedTenants[brandGroup.value],
                      ""
                    ).includes(tenant.id)
                )
                .map((tenant, index) => (
                  <TenantItem
                    brandIndex={brandIndex}
                    existingTenantList={existingTenantList}
                    hideDeleteIcon={hideDeleteIcon}
                    tenant={tenant}
                    index={index}
                    key={index}
                    data={data}
                    handleSubsidyChange={handleSubsidyChange}
                    canInputIndividual={canInputIndividual}
                    removeTenant={removeTenant}
                  />
                ))
            ) : (
              <TableRow key="0">
                <TableCell>No records found.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </ResponsiveTable>
      </Box>
      <CardSpacer />
      <ToggleStoreButton
        index={brandIndex}
        toggleDisplayStore={() => setDisplayStore(!displayStore)}
        displayStore={displayStore}
      />
    </Box>
  );
};

interface TenantItemProps {
  tenant: Tenant;
  index: number;
  brandIndex: number;
  data: FormData;
  hideDeleteIcon: boolean;
  existingTenantList: any[];
  handleSubsidyChange: (target, type, FormEvent) => void;
  canInputIndividual: boolean;
  removeTenant: (tenantId) => void;
}

const TenantItem: React.FC<TenantItemProps> = props => {
  const {
    tenant,
    brandIndex,
    index,
    data,
    existingTenantList,
    handleSubsidyChange,
    canInputIndividual,
    hideDeleteIcon,
    removeTenant
  } = props;

  const tenantlist = tenant?.storeName.replace(/\s+/g, "");
  const isExistingTenant = existingTenantList.includes(tenantlist);
  const classes = useStyles(props);

  return (
    <TableRow key={index} className={classes.tenantRow}>
      <TableCell
        className={classes.colStoreName}
        id={`brand${brandIndex}Store${index}Name`}
      >
        <Typography component="span">{tenant.storeName}</Typography>
        <span
          className={classes.blueDot}
          style={{ display: isExistingTenant ? null : "none" }}
        >
          {String.fromCharCode(8226)}
        </span>
      </TableCell>
      {hideDeleteIcon || !canInputIndividual ? null : (
        <>
          <TableCell className={classes.colPrice}>
            <TextField
              id={`brand${brandIndex}store${index}TenantDiscountTxt`}
              disabled={!canInputIndividual}
              defaultValue={0}
              label="Tenant Discount"
              fullWidth
              name={tenant.id}
              value={maybe(() => data.tenantShares[tenant.id], "0")}
              onKeyPress={event => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              onChange={event =>
                handleSubsidyChange("singleTenant", "tenant", event)
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography component="span" variant="caption">
                      %
                    </Typography>
                  </InputAdornment>
                )
              }}
            />
          </TableCell>
          <TableCell className={classes.colPrice}>
            <TextField
              id={`brand${brandIndex}store${index}SMODiscountTxt`}
              disabled={!canInputIndividual}
              label="SMOP Discount"
              fullWidth
              name={tenant.id}
              value={maybe(() => data.tenantSmoShares[tenant.id], "100")}
              onChange={event =>
                handleSubsidyChange("singleTenant", "smo", event)
              }
              onKeyPress={event => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography component="span" variant="caption">
                      %
                    </Typography>
                  </InputAdornment>
                )
              }}
            />
          </TableCell>
          <TableCell
            className={classes.colActions}
            style={{ display: isExistingTenant ? "none" : null }}
          >
            <Button
              id={`brand${brandIndex}store${index}DeleteBtn`}
              disabled={hideDeleteIcon}
              className={classes.buttonAction}
              onClick={() => removeTenant(tenant.id)}
            >
              <DeleteIcon color="primary" />
            </Button>
          </TableCell>
        </>
      )}
    </TableRow>
  );
};

const ToggleStoreButtonLine: React.FC = () => (
  <Box
    style={{
      backgroundColor: "#c4c4c4",
      flex: 1,
      height: "1px"
    }}
  />
);

interface ToggleStoreButtonProps {
  displayStore: boolean;
  index: number;
  toggleDisplayStore: () => void;
}

const ToggleStoreButton: React.FC<ToggleStoreButtonProps> = props => {
  const { displayStore, toggleDisplayStore, index } = props;
  const classes = useStyles(props);

  return (
    <Box
      style={{
        alignItems: "center",
        display: "flex",
        flexDirection: "row"
      }}
    >
      <ToggleStoreButtonLine />
      <Button
        id={`toggleStoresBtn${index}`}
        className={classes.storeButton}
        color="primary"
        variant="contained"
        onClick={toggleDisplayStore}
      >
        {displayStore ? "Hide Stores" : "Show Stores"}
      </Button>
      <ToggleStoreButtonLine />
    </Box>
  );
};

BrandGroupDialog.displayName = "BrandGroupDialog";
export default BrandGroupDialog;
